// src/pages/shop/ShopPurchaseList.jsx
//
// История приходов + детали накладной (два компонента в одном файле —
// как это сделано для оптовых отгрузок).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { purchases as purchasesApi } from '../../api';
import { useToast } from '../../hooks/useToast';
import { useMyCurrency } from '../../hooks/useMyShop';
import { formatMoney } from '../../data/currencies';
import { errorMessage } from '../../utils/errorMessage';
import Icon from '../../components/Icon';
import { useDeletePin } from '../../hooks/useDeletePin';

// ═══════════════════════════════════════════════════════════════
// СПИСОК ПРИХОДОВ
// ═══════════════════════════════════════════════════════════════
export function ShopPurchaseList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { show, ToastView } = useToast();
  const currency = useMyCurrency();
  const money = (v) => formatMoney(v, currency);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await purchasesApi.list();
        setItems(r?.items || []);
      } catch (e) {
        show(errorMessage(e, t), 'error');
      }
      setLoading(false);
    })();
  }, []); // eslint-disable-line

  return (
    <div className="page">
      <div className="page-header">
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', padding: 8, marginLeft: -8, cursor: 'pointer', color: 'var(--fg-muted)' }}
          aria-label={t('common.back')}
        >
          <Icon name="chevronLeft" size={22} stroke={2.5} />
        </button>
        <div style={{ flex: 1 }}>
          <p className="page-subtitle">{t('purchase.subtitle')}</p>
          <h1 className="page-title">{t('purchase.history')}</h1>
        </div>
        <button
          onClick={() => navigate('/purchase')}
          style={{
            width: 40, height: 40, borderRadius: 'var(--r-sm)',
            background: 'var(--brand-primary)', border: 'none', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}
          aria-label={t('purchase.title')}
        >
          <Icon name="plus" size={20} stroke={2.5} />
        </button>
      </div>

      {loading ? (
        <p style={{ padding: 24, color: 'var(--fg-muted)' }}>{t('common.loading')}</p>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 24px' }}>
          <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{t('purchase.empty')}</p>
          <p style={{ fontSize: 13, color: 'var(--fg-muted)', lineHeight: 1.5 }}>{t('purchase.emptyHint')}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map(p => (
            <button
              key={p.id}
              onClick={() => navigate(p.status === 'pending' ? `/purchase/${p.id}/receive` : `/purchase/${p.id}`)}
              className="card"
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, textAlign: 'left', cursor: 'pointer', border: 'none' }}
            >
              <div style={{
                width: 40, height: 40, borderRadius: 'var(--r-sm)', flexShrink: 0,
                background: p.status === 'pending' ? 'var(--warning-soft, #FEF3C7)' : 'var(--brand-primary-soft)',
                color: p.status === 'pending' ? 'var(--warning, #F59E0B)' : 'var(--brand-primary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon name={p.status === 'pending' ? 'refresh' : 'download'} size={18} stroke={2} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-display)' }}>
                  {t('purchase.invoiceLabel', { number: p.number })}
                </div>
                <div style={{ fontSize: 12, color: 'var(--fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.counterparty?.name} · {p.itemsCount} {t('wholesale.itemsCount')}
                </div>
                {p.status === 'pending' && (
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--warning, #F59E0B)', marginTop: 2 }}>
                    {t('receive.waiting')}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{money(p.total)}</div>
                <div style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
                  {new Date(p.createdAt).toLocaleDateString()}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {ToastView}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ДЕТАЛИ НАКЛАДНОЙ
// ═══════════════════════════════════════════════════════════════
export function ShopPurchaseDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { show, ToastView } = useToast();
  const currency = useMyCurrency();
  const money = (v) => formatMoney(v, currency);

  const { confirm: confirmDelete, PinModal } = useDeletePin();

  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setInv(await purchasesApi.get(id));
      } catch (e) {
        show(errorMessage(e, t), 'error');
        navigate('/purchases', { replace: true });
      }
      setLoading(false);
    })();
  }, [id]); // eslint-disable-line

  const cancel = async () => {
    // Отмена прихода — опасное действие (откатывает остатки), просим PIN
    const ok = await confirmDelete({
      title: t('purchase.cancelBtn'),
      message: t('purchase.confirmCancel', { number: inv?.number || '' }),
    });
    if (!ok) return;
    setBusy(true);
    try {
      await purchasesApi.remove(id);
      show(t('purchase.cancelled'));
      navigate('/purchases', { replace: true });
    } catch (e) {
      show(errorMessage(e, t), 'error');
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="page"><p style={{ padding: 24, color: 'var(--fg-muted)' }}>{t('common.loading')}</p></div>;
  }
  if (!inv) return null;

  return (
    <div className="page">
      <div className="page-header">
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', padding: 8, marginLeft: -8, cursor: 'pointer', color: 'var(--fg-muted)' }}
          aria-label={t('common.back')}
        >
          <Icon name="chevronLeft" size={22} stroke={2.5} />
        </button>
        <div style={{ flex: 1 }}>
          <p className="page-subtitle">{t('purchase.subtitle')}</p>
          <h1 className="page-title">{t('purchase.invoiceLabel', { number: inv.number })}</h1>
        </div>
      </div>

      {/* Поставщик и дата */}
      <div className="card" style={{ marginBottom: 12 }}>
        <Row label={t('purchase.supplier')} value={inv.counterparty?.name} />
        <Row label={t('audit.when')} value={new Date(inv.createdAt).toLocaleString()} />
        {inv.note && <Row label={t('purchase.note')} value={inv.note} />}
      </div>

      {/* Позиции */}
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', letterSpacing: '0.06em', marginBottom: 10 }}>
          {t('purchase.itemsHeader')}
        </p>
        {inv.items.map(i => (
          <div key={i.id} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            paddingBottom: 10, marginBottom: 10, borderBottom: '1px solid var(--border)',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                {i.name}
                {i.variantName && <span style={{ color: 'var(--fg-muted)', fontWeight: 500 }}> · {i.variantName}</span>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                {i.qty} {i.unit} × {money(i.costPrice)}
                {/* Фаза 2: если поставщик заявлял другое количество — покажем расхождение */}
                {i.qtyExpected != null && i.qtyExpected !== i.qty && (
                  <span style={{ color: 'var(--danger)', fontWeight: 600 }}>
                    {' · '}{t('purchase.expected')}: {i.qtyExpected}
                  </span>
                )}
              </div>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, flexShrink: 0 }}>{money(i.total)}</div>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>{t('purchase.total')}</span>
          <span style={{ fontSize: 18, fontWeight: 800, fontFamily: 'var(--font-display)' }}>{money(inv.total)}</span>
        </div>
      </div>

      {/* Отмена */}
      <button
        className="btn btn-danger"
        onClick={cancel}
        disabled={busy}
        style={{ width: '100%' }}
      >
        {t('purchase.cancelBtn')}
      </button>
      <p style={{ fontSize: 11, color: 'var(--fg-faint)', textAlign: 'center', marginTop: 8, lineHeight: 1.4 }}>
        {t('purchase.cancelHint')}
      </p>

      {PinModal}
      {ToastView}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0' }}>
      <span style={{ fontSize: 13, color: 'var(--fg-muted)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  );
}
